// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 (Agent E) — typed wrappers over the CareRx billing calls in web/src/api/client.ts for the
// /pot/billing screens. The client declares these `any[]`; the row shapes live in ../potApi.ts.
// The LOA status call lives in api/carerxBilling.ts (shared with the CareRx Billing tab).
import {
  createCareRxAgency, createCareRxLoa, createCareRxRateCard, generateCareRxInvoice, listCareRxAgencies,
  listCareRxInvoices, listCareRxLoas, listCareRxRateCards, listCareRxVisits, openCareRxDocument,
  renderCareRxInvoice, renderCareRxLoa, reviewCareRxInvoice, updateCareRxAgency, updateCareRxRateCard, archiveCareRxRateCard,
} from '../../../api/client'
import {
  fanOutPotAgencies, type PotAgency, type PotInvoice, type PotInvoiceAction, type PotLoa, type PotRateCard, type PotVisitRow,
} from '../potApi'

export const listPotAgencies = async (): Promise<PotAgency[]> => (await listCareRxAgencies()).items as PotAgency[]

export const listPotRateCards = async (agencyId: string): Promise<PotRateCard[]> =>
  ((await listCareRxRateCards(agencyId)).items as Omit<PotRateCard, 'agency_id'>[]).map(c => ({ ...c, agency_id: agencyId }))
export const listAllPotRateCards = (agencies: PotAgency[]) => fanOutPotAgencies(agencies, listPotRateCards)

export const listPotLoas = async (agencyId: string): Promise<PotLoa[]> =>
  ((await listCareRxLoas(agencyId)).items as Omit<PotLoa, 'agency_id'>[]).map(l => ({ ...l, agency_id: agencyId }))
export const listAllPotLoas = (agencies: PotAgency[]) => fanOutPotAgencies(agencies, listPotLoas)

export const listPotInvoices = async (agencyId: string): Promise<PotInvoice[]> =>
  ((await listCareRxInvoices(agencyId)).items as Omit<PotInvoice, 'agency_id'>[]).map(i => ({ ...i, agency_id: agencyId }))
export const listAllPotInvoices = (agencies: PotAgency[]) => fanOutPotAgencies(agencies, listPotInvoices)

/** GET carerx/visits — all visits (optionally one agency / unbilled only). */
export const listPotBillingVisits = async (agencyId?: string, unbilled?: boolean): Promise<{ items: PotVisitRow[]; truncated: boolean }> => {
  const r = await listCareRxVisits(agencyId, unbilled) as { items: PotVisitRow[]; truncated?: boolean }
  return { items: r.items, truncated: Boolean(r.truncated) }
}

export interface PotAgencyCreate {
  name: string; city?: string; state_code?: string; contact_name?: string; contact_email?: string; contact_phone?: string
  address_line?: string; zip_code?: string; agency_type?: 'hha' | 'nurse_independent'; ein?: string; w9_on_file?: boolean
}
export const createPotAgency = (b: PotAgencyCreate) => createCareRxAgency(b)

export interface PotRateCardCreate {
  effective_from: string; effective_to?: string | null
  per_visit: { first2hrs: number; additionalPerHr: number; admission: number; restart: number }
  mileage_per_mile?: number; after_hours_pct?: number; weekend_pct?: number; holiday_pct?: number
  prn_rate?: number; admin_fee?: number; min_visit_fee?: number; notes?: string; per_hour?: number
}
export const createPotRateCard = (agencyId: string, b: PotRateCardCreate) => createCareRxRateCard(agencyId, b)
/** effective_to '' clears it (open-ended). */
export const updatePotRateCard = (rateCardId: string, b: Omit<PotRateCardCreate, 'effective_to'> & { effective_to: string }) => updateCareRxRateCard(rateCardId, b)
export const archivePotRateCard = (rateCardId: string) => archiveCareRxRateCard(rateCardId)
export const updatePotAgency = (agencyId: string, b: Partial<PotAgencyCreate> & { status?: 'active' | 'inactive' }) => updateCareRxAgency(agencyId, b)

export interface PotLoaCreate { rate_card_id: string; start_date: string; end_date?: string | null; patient_initials?: string; patient_mrn?: string }
export const createPotLoa = (agencyId: string, b: PotLoaCreate) => createCareRxLoa(agencyId, b)
export { LOA_NEXT, setCareRxLoaStatus as setPotLoaStatus } from '../../../api/carerxBilling'

export interface PotInvoiceGenerated {
  id: string; invoice_number: string; visit_charges: number; mileage_charges: number; premium_charges: number
  admin_fee: number; total: number; visit_count: number
}
export const generatePotInvoice = (agencyId: string, period: { period_start: string; period_end: string }) =>
  generateCareRxInvoice(agencyId, period) as Promise<PotInvoiceGenerated>

export const reviewPotInvoice = (invoiceId: string, action: PotInvoiceAction, notes?: string) =>
  reviewCareRxInvoice(invoiceId, { action, notes })

/** The ASP "PDF" link: OwlRx renders the document on demand and stores it; then we open it. */
export async function openPotBillingPdf(kind: 'invoice' | 'loa', id: string): Promise<'opened' | 'not_stored'> {
  const r = kind === 'invoice' ? await renderCareRxInvoice(id) : await renderCareRxLoa(id)
  if (!r.stored) return 'not_stored'
  await openCareRxDocument(r.document_id)
  return 'opened'
}
